import * as React from 'react'
import * as ReactDOMServer from 'react-dom/server'

const BOXOVER_THEME = {
  default: { body: 'tooltip_bdy', header: 'tooltip_hdr' },
  chart: { body: 'hoverchart', header: 'tabchrthdr' },
}

interface BoxoverProps {
  children: React.ReactNode

  /**
   * Which tooltip design to use
   *
   * @default default
   */
  theme?: keyof typeof BOXOVER_THEME

  /**
   * Tooltip header
   */
  header?: string

  /**
   * Tooltip body, can be any react node
   */
  body: React.ReactNode

  /**
   * Delay in ms to show the tooltip
   *
   * @default 300
   */
  delay?: number

  offsetX?: number
  offsetY?: number
}

/**
 * This component composes boxover and forwards `data-boxover` prop to children
 */
export function Boxover({
  children,
  theme = 'default',
  header,
  body,
  delay = 300,
  offsetX = 100,
  offsetY = 100,
}: BoxoverProps) {
  const boxover = React.useMemo(
    () =>
      [
        `cssbody=[${BOXOVER_THEME[theme].body}]`,
        `cssheader=[${BOXOVER_THEME[theme].header}]`,
        header && `header=[${header}]`,
        `body=[${React.isValidElement(body) ? ReactDOMServer.renderToString(body) : body}]`,
        `delay=[${delay}]`,
        `offsetx=[${offsetX}]`,
        `offsety=[${offsetY}]`,
      ]
        .filter(Boolean)
        .join(' '),
    [theme, header, body, delay, offsetX, offsetY]
  )

  return React.Children.map(children, (child) =>
    React.isValidElement(child)
      ? React.cloneElement(child as React.ReactElement<{ 'data-boxover': string }>, {
          'data-boxover': boxover,
        })
      : child
  ) as unknown as JSX.Element
}
